import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { doc } from "firebase/firestore";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  CalendarDays,
  ClipboardClock,
  ExternalLink,
  LayoutDashboard,
  UserCheck,
  UsersRound,
  Award,
  Bell,
  BarChart3,
  Palette,
  LifeBuoy,
  MapPin,
  Users,
  Plane,
  FileSpreadsheet,
} from "lucide-react";
import { DRAWER_WIDTH } from "../constants/layout";
import { db } from "../lib/firebase";
import { useFirestoreDocOnce } from "../hooks/useFirestoreDocOnce";
import {
  isSharePointConfigured,
  getSharePointHomeUrl,
} from "../utils/sharepoint";
import { EventSwitcher } from "./EventSwitcher";
import type { Event } from "@event-planner/shared";

/**
 * Event-scoped nav items. Always rendered, regardless of whether an event is
 * currently selected. When no event is active, clicking an item pops the
 * event switcher as a hint instead of navigating — teaches the user where
 * context is changed without the bait-and-switch of a disabled-looking-but-
 * clickable row.
 */
// Surveys and Check-in are sub-views of Analytics — surveys (replacing
// feedback, ADR-054), check-in dashboards. Their routes live under
// /analytics/... and are reached via in-page navigation, not the sidebar.
// `slug` is the stable testID suffix (`event-nav-{slug}`) — derived from
// the path with the leading slash stripped, defaulting to "overview" for
// the empty path. Kept explicit so test selectors don't drift if a path
// is renamed.
//
// Order mirrors the Phase 0 / Phase 1 / Phase 2 organizer workflow:
//   - Phase 1 (per-event setup): Roster, Locations
//   - Phase 2 (per-event content): Groups, Agenda, Travel
// Foreign-key dependencies dictate the ordering — Sessions reference
// Locations + Groups + Users; Travel with `sessionLink` references
// Sessions. Putting Travel after Agenda avoids the Travel-import
// "Unknown session" hard-block. Notifications + analytics live below
// as ongoing/derivative tools. Sponsors sits at the end during Beta
// because the feature is still under construction.
//
// Sheet/file naming note: "Attendees" is the per-event surface concept
// (the page heading and route URL stay /attendees), but the sidebar
// nav label, canonical workbook sheet, and import dialog title all
// read "Roster" because that's the FRG-natural term for a list of
// people for a specific event.
export const EVENT_NAV_ITEMS = [
  { label: "Overview", path: "", icon: LayoutDashboard, slug: "overview" },
  { label: "Roster", path: "/attendees", icon: UserCheck, slug: "attendees" },
  { label: "Locations", path: "/locations", icon: MapPin, slug: "locations" },
  { label: "Groups", path: "/groups", icon: Users, slug: "groups" },
  { label: "Agenda", path: "/agenda", icon: ClipboardClock, slug: "agenda" },
  { label: "Travel", path: "/travel", icon: Plane, slug: "travel" },
  { label: "Notifications", path: "/notifications", icon: Bell, slug: "notifications" },
  { label: "Analytics", path: "/analytics", icon: BarChart3, slug: "analytics" },
  { label: "Planning Workbook", path: "/planning-workbook", icon: FileSpreadsheet, slug: "planning-workbook" },
  { label: "Sponsors", path: "/sponsors", icon: Award, slug: "sponsors" },
];

interface SidebarProps {
  /**
   * Event that should drive the switcher label and scoped-item link targets.
   * Stays set after the user navigates out to /users or /events so they can
   * jump back into the same event's pages without re-picking. Null only
   * before the user has entered any event in the current session.
   */
  activeEventId: string | null;
}

/**
 * Desktop shell sidebar: a permanent drawer beside the page. The phone shell
 * (components/phone/PhoneShell.tsx) shows the same SidebarContent in a
 * slide-in drawer instead.
 */
export function Sidebar({ activeEventId }: SidebarProps) {
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <SidebarContent activeEventId={activeEventId} />
    </Drawer>
  );
}

/** Logo, main nav, event switcher, event nav and help footer. */
export function SidebarContent({ activeEventId }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  // Fetch the active event for the switcher label. Separate from EventLayout's
  // fetch (which scopes EventContext to pages under /events/:eventId) because
  // Sidebar sits in DashboardLayout, above EventLayout, and we want the label
  // to persist across /users and /events navigation — not just while inside
  // an event's sub-routes.
  const eventDocRef = useMemo(
    () => (activeEventId ? doc(db, "events", activeEventId) : null),
    [activeEventId],
  );
  const { data: currentEvent } = useFirestoreDocOnce<Event>(eventDocRef);

  function handleScopedItemClick(fullPath: string | null, itemPath: string) {
    if (fullPath) {
      navigate(fullPath);
    } else {
      setPendingPath(itemPath);
      setSwitcherOpen(true);
    }
  }

  return (
    <>
      <Box
        sx={{
          height: 102,
          backgroundImage: "url(/fr-mountain.png)",
          backgroundSize: "cover",
          backgroundPosition: "center top",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src="/frg-logo-white.png"
          alt="Flat Rock Global"
          sx={{
            // 1324x317 wordmark; 192px wide ≈ 46px tall, centered in the
            // 102px header with breathing room inside the 240px drawer.
            width: 192,
            height: "auto",
            filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.5))",
          }}
        />
      </Box>
      <Divider />
      <Box sx={{ overflow: "auto", flexGrow: 1 }}>
        <nav aria-label="Main navigation">
          <List>
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/users"
                selected={location.pathname.startsWith("/users")}
                aria-current={
                  location.pathname.startsWith("/users") ? "page" : undefined
                }
                data-testid="main-nav-users"
              >
                <ListItemIcon>
                  <UsersRound size={20} />
                </ListItemIcon>
                <ListItemText primary="Users" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/events"
                selected={
                  location.pathname === "/events" ||
                  location.pathname === "/events/new"
                }
                aria-current={
                  location.pathname === "/events" ||
                  location.pathname === "/events/new"
                    ? "page"
                    : undefined
                }
                data-testid="main-nav-events"
              >
                <ListItemIcon>
                  <CalendarDays size={20} />
                </ListItemIcon>
                <ListItemText primary="Events" />
              </ListItemButton>
            </ListItem>
            {isSharePointConfigured() && (
              <ListItem disablePadding>
                <ListItemButton
                  component="a"
                  href={getSharePointHomeUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="main-nav-sharepoint"
                >
                  <ListItemIcon>
                    <ExternalLink size={20} />
                  </ListItemIcon>
                  <ListItemText primary="SharePoint" />
                </ListItemButton>
              </ListItem>
            )}
            {/*
              Dev-only Design System link. `import.meta.env.DEV` is Vite's
              equivalent of React Native's `__DEV__` — a compile-time
              constant replaced with `false` in production builds. Rollup
              then dead-code-eliminates the entire branch (and, because
              nothing else references `Palette`, drops the icon import too).
            */}
            {import.meta.env.DEV && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/design-system"
                  selected={location.pathname === "/design-system"}
                  aria-current={
                    location.pathname === "/design-system" ? "page" : undefined
                  }
                  data-testid="main-nav-design-system"
                >
                  <ListItemIcon>
                    <Palette size={20} />
                  </ListItemIcon>
                  <ListItemText primary="Design System" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </nav>
        <Divider />
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 0.8 }}
          >
            Event
          </Typography>
        </Box>
        <EventSwitcher
          currentEvent={currentEvent}
          open={switcherOpen}
          onOpenChange={(open) => {
            setSwitcherOpen(open);
            if (!open) setPendingPath(null);
          }}
          pendingPath={pendingPath}
        />
        <nav aria-label="Event navigation" >
          <Divider sx={{pt: 2}}/>
          <List>
            {EVENT_NAV_ITEMS.map((item) => {
              const fullPath = activeEventId
                ? `/events/${activeEventId}${item.path}`
                : null;
              const isActive =
                !!fullPath &&
                (item.path === ""
                  ? location.pathname === fullPath
                  : location.pathname.startsWith(fullPath));
              const Icon = item.icon;

              return (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    onClick={() => handleScopedItemClick(fullPath, item.path)}
                    selected={isActive}
                    aria-current={isActive ? "page" : undefined}
                    aria-disabled={!activeEventId}
                    data-testid={`event-nav-${item.slug}`}
                    sx={{
                      color: activeEventId ? "text.primary" : "text.secondary",
                      "& .MuiListItemIcon-root": {
                        color: activeEventId ? "inherit" : "text.disabled",
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Icon size={20} />
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </nav>
      </Box>
      {/*
        Help footer pinned to the bottom of the sidebar via the parent Drawer
        paper's flex column + the scroll region's flexGrow above. Sits outside
        the overflow: auto box so it stays visible regardless of how many
        event-scoped nav items push the scroll region.
      */}
      <Divider />
      <nav aria-label="Help">
        <List>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/support"
              selected={location.pathname === "/support"}
              aria-current={location.pathname === "/support" ? "page" : undefined}
            >
              <ListItemIcon>
                <LifeBuoy size={20} />
              </ListItemIcon>
              <ListItemText primary="Help & Support" />
            </ListItemButton>
          </ListItem>
        </List>
      </nav>
    </>
  );
}
